import axios from "axios";
import { getApiUrlWithVersion } from "./environmentService";
import { getAuthUser } from "./authService";
import { Journey, JourneyStep, JourneyEnrollment, JourneyEnrollmentsResponse, StepExecutionsResponse } from "../types/journey";

class JourneysService {
  private getAuthHeaders(): object {
    return {
      Authorization: getAuthUser()?.accessToken,
    };
  }

  async listJourneys(): Promise<Journey[]> {
    const response = await axios.get(`${getApiUrlWithVersion()}/journeys`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getJourney(id: string): Promise<Journey> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/journeys/${id}`,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async createJourney(name: string): Promise<Journey> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/journeys`,
      { name },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateJourney(id: string, name: string): Promise<Journey> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/journeys/${id}`,
      { name },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async deleteJourney(id: string): Promise<void> {
    await axios.delete(`${getApiUrlWithVersion()}/journeys/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async activateJourney(id: string): Promise<Journey> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${id}/activate`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async pauseJourney(id: string): Promise<Journey> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${id}/pause`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async archiveJourney(id: string): Promise<Journey> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${id}/archive`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async setWelcomeJourney(id: string): Promise<Journey> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${id}/set_welcome`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async unsetWelcomeJourney(id: string): Promise<Journey> {
    const response = await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${id}/unset_welcome`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getEnrollments(journeyId: string, page: number = 1): Promise<JourneyEnrollmentsResponse> {
    const response = await axios.get(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/enrollments`,
      { headers: this.getAuthHeaders(), params: { page } }
    );
    return response.data;
  }

  async createStep(
    journeyId: string,
    step: {
      step_type?: "email" | "cancel_journey" | "add_to_segment" | "remove_from_segment";
      newsletter_edition_id?: string;
      cancels_journey_id?: string;
      segment_id?: string;
      position: number;
      delay_amount: number;
      delay_unit: string;
    }
  ): Promise<JourneyStep> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/steps`,
      step,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async updateStep(
    journeyId: string,
    stepId: string,
    step: {
      newsletter_edition_id?: string;
      position?: number;
      delay_amount?: number;
      delay_unit?: string;
      step_type?: string;
      cancels_journey_id?: string;
      segment_id?: string;
    }
  ): Promise<JourneyStep> {
    const response = await axios.put(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/steps/${stepId}`,
      step,
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async reorderSteps(journeyId: string, stepIds: string[]): Promise<void> {
    await axios.patch(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/steps/reorder`,
      { step_ids: stepIds },
      { headers: this.getAuthHeaders() }
    );
  }

  async deleteStep(journeyId: string, stepId: string): Promise<void> {
    await axios.delete(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/steps/${stepId}`,
      { headers: this.getAuthHeaders() }
    );
  }

  async enrollSubscriber(journeyId: string, email: string): Promise<{ message: string }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/enroll`,
      { email },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async enrollSegment(journeyId: string, segmentId: string): Promise<{ message: string; enrolled_count: number }> {
    const response = await axios.post(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/enroll_segment`,
      { segment_id: segmentId },
      { headers: this.getAuthHeaders() }
    );
    return response.data;
  }

  async getStepExecutions(journeyId: string, stepId: string, status?: string): Promise<StepExecutionsResponse> {
    const params: Record<string, string> = {};
    if (status) params.status = status;
    const response = await axios.get(
      `${getApiUrlWithVersion()}/journeys/${journeyId}/steps/${stepId}/executions`,
      { headers: this.getAuthHeaders(), params }
    );
    return response.data;
  }
}

const journeysService = new JourneysService();
export default journeysService;
